import { getPageKey } from './utils/pageKey';
import { buildContext } from './utils/context';
import { getDefaultUiTargetText, CLICK_DEBOUNCE_MS, PAGE_VIEW_DEBOUNCE_MS } from './utils/constants';
import { track } from './ga4';
import { writeActionLog, updateDailyCounter, updateTotalCounter } from './firestore';

/**
 * 페이지 뷰 중복 방지 (같은 페이지에서 연속 호출 방지)
 */
let lastPageView = null;

// UI 클릭 디바운스
const clickDebounceMap = new Map();

/**
 * 페이지 뷰 추적
 * @param {string} pageTitle - 페이지 제목 (없으면 document.title 사용)
 * @param {object} authStateOverride - Recoil authState를 직접 전달 (선택, localStorage보다 우선)
 */
export const trackPageView = (pageTitle = null, authStateOverride = null) => {
  const title = pageTitle || (typeof document !== 'undefined' ? document.title : '');
  
  const pathname = typeof window !== 'undefined' && window.location ? window.location.pathname : '';
  const pageKey = getPageKey(pathname);
  const pageLocation = typeof window !== 'undefined' && window.location ? window.location.href : '';
  
  // 중복 방지: 같은 페이지에서 1초 내 연속 호출 방지
  const now = Date.now();
  const pageViewKey = `${pathname}_${pageKey}`;
  if (lastPageView && lastPageView.key === pageViewKey && (now - lastPageView.time) < PAGE_VIEW_DEBOUNCE_MS) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Page view debounced:', pageViewKey);
    }
    return;
  }
  lastPageView = { key: pageViewKey, time: now };
  
  // GA4 추적
  track('page_view', {
    page_title: title,
    page_key: pageKey || 'unknown',
    page_path: pathname || '',
    page_location: pageLocation || '',
  }, authStateOverride);
  
  // Firestore 로깅 (비동기, 에러가 발생해도 GA4 추적은 계속)
  const context = buildContext({}, authStateOverride);
  if (context.gc_id) {
    // action_logs에 기록
    writeActionLog({
      event_type: 'page_view',
      gc_id: context.gc_id,
      gc_name: context.gc_name,
      page_key: pageKey || 'unknown',
      page_path: pathname || '',
      page_location: pageLocation || '',
      ui_target_key: null,
      ui_target_text: null,
      ui_area: null,
      user_role: context.user_role,
      is_master: context.is_master,
      element_type: null,
      element_id: null,
    });
    
    // 집계 업데이트
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, ''); // yyyymmdd
    updateDailyCounter(date, context, 'page_view');
    updateTotalCounter(context, 'page_view');
  }
};

/**
 * UI 클릭 이벤트 추적
 * @param {object} payload - 클릭 이벤트 페이로드
 * @param {string} payload.element_type - 요소 타입 ('button' | 'tab' | 'menu' | 'link' | 'card')
 * @param {string} payload.element_id - 요소 ID (필수, 고정 문자열)
 * @param {string} payload.element_text - 표시 텍스트
 * @param {string} payload.ui_area - UI 영역 (sidebar/top_card/journey/table/modal/page 등)
 * @param {string} payload.ui_target_key - UI 타겟 키 (필수, snake_case)
 * @param {string} payload.ui_target_text - UI 타겟 텍스트 (선택, 없으면 자동 생성)
 * @param {object} payload.authStateOverride - Recoil authState 직접 전달 (선택)
 */
export const trackUiClick = (payload) => {
  const { element_type, element_id, element_text, ui_area, ui_target_key, ui_target_text, authStateOverride, ...additionalParams } = payload;

  // element_id는 절대 비어있으면 안 됨
  if (!element_id || element_id.trim() === '') {
    console.error('[Analytics] trackUiClick: element_id is required and cannot be empty');
    return;
  }

  // ui_target_key는 필수
  if (!ui_target_key || ui_target_key.trim() === '') {
    console.error('[Analytics] trackUiClick: ui_target_key is required and cannot be empty');
    return;
  }

  // ui_target_text가 없으면 자동 생성
  const finalUiTargetText = ui_target_text || getDefaultUiTargetText(ui_target_key);

  // 디바운스: 같은 element_id에서 300ms 내 연속 클릭 방지
  const now = Date.now();
  const debounceKey = `${element_id}_${ui_target_key}`;
  const lastClick = clickDebounceMap.get(debounceKey);
  if (lastClick && (now - lastClick) < CLICK_DEBOUNCE_MS) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] UI click debounced:', debounceKey);
    }
    return;
  }
  clickDebounceMap.set(debounceKey, now);

  // GA4 추적
  track('ui_click', {
    element_type: element_type || 'button',
    element_id,
    element_text: element_text || '',
    action: 'click',
    ui_area: ui_area || 'page',
    ui_target_key,
    ui_target_text: finalUiTargetText,
    ...additionalParams,
  }, authStateOverride);

  // Firestore 로깅 (비동기, 에러가 발생해도 GA4 추적은 계속)
  const context = buildContext({}, authStateOverride);
  if (context.gc_id) {
    const pathname = typeof window !== 'undefined' && window.location ? window.location.pathname : '';
    const pageKey = getPageKey(pathname);
    const pageLocation = typeof window !== 'undefined' && window.location ? window.location.href : '';
    
    // action_logs에 기록
    writeActionLog({
      event_type: 'ui_click',
      gc_id: context.gc_id,
      gc_name: context.gc_name,
      page_key: pageKey || 'unknown',
      page_path: pathname || '',
      page_location: pageLocation || '',
      ui_target_key,
      ui_target_text: finalUiTargetText,
      ui_area: ui_area || null,
      user_role: context.user_role,
      is_master: context.is_master,
      element_type: element_type || 'button',
      element_id,
    });
    
    // 집계 업데이트
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, ''); // yyyymmdd
    updateDailyCounter(date, context, 'ui_click', ui_target_key);
    updateTotalCounter(context, 'ui_click', ui_target_key);
  }
};

/**
 * 로그인 성공 이벤트 추적
 * @param {object} user - 사용자 정보 객체
 */
export const trackLoginSuccess = (user) => {
  // gc_id는 항상 문자열로 변환 (BigQuery에서 string_value로 저장되도록 강제)
  const gcIdValue = user?.club_id;
  const gcId = gcIdValue != null ? String(gcIdValue) : null;
  const userId = gcId || (user?.id != null ? String(user.id) : null);
  
  const isMaster = Number(user?.club_admin_level) === 10;
  
  // gc_name도 항상 문자열로 보장
  const gcNameValue = isMaster ? 'CEO' : (user?.club_name || user?.name);
  const gcName = gcNameValue != null ? String(gcNameValue) : null;

  // user_id 설정 (gtag config)
  const ga4Id = process.env.REACT_APP_GA4_ID;
  if (userId && typeof window !== 'undefined' && typeof window.gtag === 'function' && ga4Id) {
    window.gtag('config', ga4Id, {
      user_id: String(userId),
    });
  }

  // 사용자 속성 설정
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('set', 'user_properties', {
      golf_course_id: gcId,
      golf_course_name: gcName,
    });
  }

  track('login_success', {
    user_id: userId,
    gc_id: gcId,
    gc_name: gcName,
    user_role: user?.club_admin_level ? String(user.club_admin_level) : null,
    is_master: isMaster,
  });
};

/**
 * 로그인 실패 이벤트 추적
 * @param {object} params - 에러 정보
 * @param {string} params.error_type - 에러 타입
 * @param {string} params.error_message - 에러 메시지
 * @param {number} params.query_len - 입력 길이 (PII 제거)
 * @param {string} params.login_id - 입력한 로그인 ID
 * @param {string} params.club_id - 골프장 ID
 * @param {string} params.club_name - 골프장 이름
 */
export const trackLoginFailure = (params) => {
  // ui_click 이벤트로 추적하여 타임라인에 표시되도록 함
  trackUiClick({
    ui_target_key: 'login_failure',
    element_id: 'login-form',
    element_text: '로그인 실패',
    ui_target_text: '로그인 실패',
    ui_area: 'page',
    gc_id: params.club_id || null,
    gc_name: params.club_name || null,
    error_type: params.error_type || 'unknown',
    error_message: params.error_message || '',
    query_len: params.query_len || 0,
    login_id: params.login_id || null,
  });
};
